/**
 * @file capture/micErrors.ts
 * @brief Friendly messages for getUserMedia / MediaRecorder failures.
 *
 * @author DES
 * @date 28-07-2026
 */

export function describeMicError(err: unknown): string {
  const name =
    err && typeof err === "object" && "name" in err
      ? String((err as { name: string }).name)
      : "";
  const message = err instanceof Error ? err.message : "";

  switch (name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
      return "Microphone permission denied. Allow mic access in the browser address bar, then try again.";
    case "NotFoundError":
    case "DevicesNotFoundError":
      return "No microphone found. Plug in a mic (or USB headset) and refresh.";
    case "NotReadableError":
    case "TrackStartError":
      return "Microphone is busy — close Zoom, Teams, or other apps using the mic, then try again.";
    case "OverconstrainedError":
      return "That microphone isn’t available. Pick another mic or use the system default.";
    case "SecurityError":
      return "Microphone blocked on this page. Use HTTPS (or localhost) and allow mic access.";
    case "AbortError":
      return "Microphone request was interrupted. Try again.";
    default:
      break;
  }

  if (/permission|denied|not allowed/i.test(message)) {
    return "Microphone permission denied. Allow mic access, then try again.";
  }
  if (/not found|no device/i.test(message)) {
    return "No microphone found. Connect a mic and try again.";
  }

  return message || "Could not open the microphone.";
}

export function describeRecorderSupportError(): string | null {
  if (typeof window === "undefined") return null;
  if (typeof MediaRecorder === "undefined") {
    return "This browser can’t record audio (MediaRecorder missing). Use Chrome, Edge, or Safari 14.1+.";
  }
  if (!navigator.mediaDevices?.getUserMedia) {
    return "This browser can’t access the microphone. Use a modern Chromium or Safari browser.";
  }
  return null;
}
